// ─── Chordsn Audio Engine v5 – Salamander Grand Piano via CDN ─────────────────
// Lädt Tone.js und Salamander-Samples dynamisch per CDN.
// Keine npm-Abhängigkeit nötig – funktioniert mit unveränderter package.json.

const TONE_CDN = "https://cdnjs.cloudflare.com/ajax/libs/tone/14.7.77/Tone.js";
const SAMPLE_BASE = "https://tonejs.github.io/audio/salamander/";

const SAMPLE_URLS = {
  "A0":"A0.mp3","C1":"C1.mp3","D#1":"Ds1.mp3","F#1":"Fs1.mp3",
  "A1":"A1.mp3","C2":"C2.mp3","D#2":"Ds2.mp3","F#2":"Fs2.mp3",
  "A2":"A2.mp3","C3":"C3.mp3","D#3":"Ds3.mp3","F#3":"Fs3.mp3",
  "A3":"A3.mp3","C4":"C4.mp3","D#4":"Ds4.mp3","F#4":"Fs4.mp3",
  "A4":"A4.mp3","C5":"C5.mp3","D#5":"Ds5.mp3","F#5":"Fs5.mp3",
  "A5":"A5.mp3","C6":"C6.mp3","D#6":"Ds6.mp3","F#6":"Fs6.mp3",
  "A6":"A6.mp3","C7":"C7.mp3","D#7":"Ds7.mp3","F#7":"Fs7.mp3",
  "A7":"A7.mp3","C8":"C8.mp3",
};

// State
let toneLoaded = false;
let sampler = null;
let samplerReady = false;
let initPromise = null;

// Tone.js per Script-Tag laden
function loadToneScript() {
  return new Promise((resolve, reject) => {
    if (window.Tone) { resolve(window.Tone); return; }
    const script = document.createElement("script");
    script.src = TONE_CDN;
    script.onload  = () => { toneLoaded = true; resolve(window.Tone); };
    script.onerror = () => reject(new Error("Tone.js CDN failed"));
    document.head.appendChild(script);
  });
}

// Sampler initialisieren (einmalig)
function initSampler() {
  if (initPromise) return initPromise;
  initPromise = new Promise(async (resolve) => {
    try {
      const Tone = await loadToneScript();
      sampler = new Tone.Sampler({
        urls: SAMPLE_URLS,
        baseUrl: SAMPLE_BASE,
        release: 1.2,
        onload: () => {
          samplerReady = true;
          resolve(sampler);
        },
        onerror: (e) => {
          console.warn("Sampler load error:", e);
          resolve(sampler);
        }
      });

      // Reverb für Konzertsaal-Atmosphäre
      const reverb = new Tone.Reverb({ decay: 2.5, preDelay: 0.01, wet: 0.2 });
      await reverb.ready;

      // EQ: etwas wärmer
      const eq = new Tone.EQ3({ low: 2, mid: 0, high: -2 });

      sampler.chain(eq, reverb, Tone.Destination);

      // Timeout-Fallback falls onload nicht feuert
      setTimeout(() => { samplerReady = true; resolve(sampler); }, 10000);

    } catch (e) {
      console.warn("Piano init failed:", e);
      resolve(null);
    }
  });
  return initPromise;
}

// Oktavlagen: Noten immer aufsteigend
const NOTE_SEMI = {
  'C':0,'C#':1,'Db':1,'D':2,'D#':3,'Eb':3,'E':4,'F':5,
  'F#':6,'Gb':6,'G':7,'G#':8,'Ab':8,'A':9,'A#':10,'Bb':10,'B':11
};

function assignOctaves(noteNames, rootOctave = 3) {
  const result = [];
  let prevSemi = -1, octave = rootOctave;
  noteNames.forEach((name, i) => {
    const semi = NOTE_SEMI[name] ?? 0;
    if (i > 0 && semi <= prevSemi) octave++;
    result.push({ name, octave });
    prevSemi = semi;
  });
  return result;
}

// Akkord spielen
export async function playChord(noteNames, duration = 2.5) {
  if (!noteNames || noteNames.length === 0) return;
  try {
    const Tone = window.Tone || await loadToneScript();
    await Tone.start();
    const piano = await initSampler();
    if (!piano) return;

    const notes = assignOctaves(noteNames, 3);
    const now   = Tone.now() + 0.02;
    const velocities = [0.85, 0.65, 0.55, 0.50];

    notes.forEach((note, i) => {
      const vel = velocities[Math.min(i, velocities.length - 1)];
      piano.triggerAttackRelease(note.name + note.octave, duration, now, vel);
    });
  } catch(e) {
    console.warn("playChord error:", e);
  }
}

// Progression spielen
export async function playProgression(chordNoteArrays, bpm = 80) {
  if (!chordNoteArrays || chordNoteArrays.length === 0) return;
  try {
    const Tone = window.Tone || await loadToneScript();
    await Tone.start();
    const piano = await initSampler();
    if (!piano) return;

    const chordDur = (60 / bpm) * 2;
    const now = Tone.now() + 0.02;
    const velocities = [0.82, 0.62, 0.52, 0.48];

    chordNoteArrays.forEach((noteNames, ci) => {
      const start = now + ci * chordDur;
      const notes = assignOctaves(noteNames, 3);
      notes.forEach((note, ni) => {
        const vel = velocities[Math.min(ni, velocities.length - 1)];
        piano.triggerAttackRelease(note.name + note.octave, chordDur * 0.85, start + ni * 0.028, vel);
      });
    });
  } catch(e) {
    console.warn("playProgression error:", e);
  }
}

export function stopAll() {
  if (sampler) { try { sampler.releaseAll(); } catch(e) {} }
}

export function preloadPiano() {
  initSampler();
}

export function isReady() {
  return samplerReady;
}
